/**
 * src/lib/observability/renderAudit.js — Per-view render reconciliation emitter.
 *
 * Views call reportRenderAudit({ view, requested, returned, rendered, excluded })
 * after every data-change cycle. A 500ms debounce coalesces rapid re-renders so
 * a single user action produces one log line per view instead of dozens.
 *
 * Identical consecutive payloads short-circuit — only state-changing audits emit.
 */

import { logEvent, getTraceContext } from '../observability';

const PHASE_TAG = 'phase_a2';
const DEBOUNCE_MS = 500;

const _pending = new Map();
const _lastEmitted = new Map();

function _signature(p) {
  return `${p.requested}|${p.returned}|${p.rendered}|${p.excluded}`;
}

export function reportRenderAudit({ view, requested = 0, returned = 0, rendered = 0, excluded = 0 } = {}) {
  if (!view) return;
  const payload = { view, requested, returned, rendered, excluded };
  const sig = _signature(payload);

  const existing = _pending.get(view);
  if (existing?.timer) clearTimeout(existing.timer);

  const timer = setTimeout(() => {
    _pending.delete(view);
    if (_lastEmitted.get(view) === sig) return;
    _lastEmitted.set(view, sig);
    logEvent('render_audit', `render_audit:${view}`, {
      phase_tag: PHASE_TAG,
      ...getTraceContext(),
      ...payload,
    });
  }, DEBOUNCE_MS);

  _pending.set(view, { payload, timer });
}

export function _resetForTest() {
  for (const { timer } of _pending.values()) clearTimeout(timer);
  _pending.clear();
  _lastEmitted.clear();
}
